import LoadingButton from '@mui/lab/LoadingButton';
import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material';
import { includes } from 'lodash';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { listCentralFillPharmaciesApi } from 'api/centralFill';
import { listCatalogApi } from 'api/drug';
import { updatePartnerCatalogApi } from 'api/partner';
import AlertPartnersCheckbox, {
  ALERT_PARTNER_CHECKBOX,
} from 'modules/CatalogManagement/components/AlertPartnersCheckbox';
import CatalogDialogHeader from 'modules/CatalogManagement/components/CatalogDialogHeader';
import {
  formatDrugName,
  formatForm,
  formatPackSize,
  formatStrength,
} from 'modules/CatalogManagement/utils/format';
import { handleError } from 'store/thunks/errorHandlerThunks';

// Mirrors e-scribe-ui's AddPartnerFormulary exactly: pick an existing MASTER
// catalog drug, then one or more of its strengths via a multi-select Strength
// dropdown (not a checkbox list), and associate them with this partner. It
// does not create a new master catalog drug (that's Catalog Management's New
// dialog).
const AddPartnerCatalogItemDialog = ({ open, partner, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [masterCatalog, setMasterCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [selectedCatalogIds, setSelectedCatalogIds] = useState([]);
  const [compoundingPharmacyId, setCompoundingPharmacyId] = useState('');
  const [pharmacies, setPharmacies] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alertPartners, setAlertPartners] = useState(false);

  useEffect(() => {
    if (!open) return;

    setSelectedDrug(null);
    setSelectedCatalogIds([]);
    setCompoundingPharmacyId('');
    setSubmitted(false);
    setAlertPartners(false);
    setLoadingCatalog(true);

    listCatalogApi({})
      .then(({ results }) => setMasterCatalog(results ?? []))
      .catch((error) => dispatch(handleError({ error })))
      .finally(() => setLoadingCatalog(false));

    listCentralFillPharmaciesApi()
      .then((results) =>
        setPharmacies(
          [...(results ?? [])].sort((left, right) =>
            String(left?.name ?? '').localeCompare(String(right?.name ?? ''), undefined, {
              sensitivity: 'base',
            })
          )
        )
      )
      .catch((error) => dispatch(handleError({ error })));
  }, [open, dispatch]);

  const drugOptions = useMemo(
    () =>
      masterCatalog
        .map((drug) => ({ ...drug, label: formatDrugName(drug.drugName) }))
        .sort((left, right) =>
          left.label.localeCompare(right.label, undefined, { sensitivity: 'base' })
        ),
    [masterCatalog]
  );

  const handleDrugChange = (_, drug) => {
    setSelectedDrug(drug);
    setSelectedCatalogIds([]);
  };

  const describeVariant = (variant) =>
    [
      formatStrength(variant.strength),
      formatPackSize(variant.packSizeValue, variant.packSizeUnit),
      formatForm(variant.formAlias, variant.dosageForm),
    ]
      .filter(Boolean)
      .join(' ');

  // Natural order, so "2.5 mg" reads before "10 mg" rather than after it.
  const sortedVariants = [...(selectedDrug?.variants ?? [])].sort((left, right) =>
    describeVariant(left).localeCompare(describeVariant(right), undefined, {
      sensitivity: 'base',
      numeric: true,
    })
  );

  const canSave = !!selectedDrug && selectedCatalogIds.length > 0;

  const handleDialogClose = (_, reason) => {
    if (saving || reason === 'backdropClick') return;
    onClose();
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!canSave) return;
    setSaving(true);

    updatePartnerCatalogApi({
      partnerUuid: partner.partnerUuid,
      partnerCatalog: selectedCatalogIds.map((catalogId) => ({
        catalogId,
        isActive: true,
        isDeleted: false,
        ...(compoundingPharmacyId ? { compoundingPharmacyId } : {}),
      })),
      alertPartners,
    })
      .then(() => {
        enqueueSnackbar('Catalog Added Successfully', { variant: 'success' });
        onClose();
        onSaved();
      })
      .catch((error) => dispatch(handleError({ error })))
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth='xs' fullWidth>
      <CatalogDialogHeader title='Add Catalog' onClose={handleDialogClose} />

      <DialogContent>
        <Autocomplete
          fullWidth
          sx={{ mt: 1 }}
          options={drugOptions}
          loading={loadingCatalog}
          value={selectedDrug}
          onChange={handleDrugChange}
          getOptionLabel={(option) => option.label ?? ''}
          isOptionEqualToValue={(option, value) => option.drugName === value.drugName}
          renderInput={(params) => (
            <TextField
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...params}
              label='Search Catalog By Drug Name'
              required
              helperText='Required'
              error={submitted && !selectedDrug}
            />
          )}
        />

        <FormControl
          fullWidth
          variant='outlined'
          required
          sx={{ mt: 3 }}
          disabled={!selectedDrug}
          error={submitted && selectedCatalogIds.length === 0}
        >
          <InputLabel id='AddPartnerCatalogItemDialog-strength-label' shrink>
            Strength
          </InputLabel>
          <Select
            labelId='AddPartnerCatalogItemDialog-strength-label'
            id='AddPartnerCatalogItemDialog-strength-input'
            multiple
            input={<OutlinedInput label='Strength' />}
            value={selectedCatalogIds}
            onChange={(event) => setSelectedCatalogIds(event.target.value)}
            renderValue={(selected) =>
              selected
                .map((catalogId) => {
                  const variant = selectedDrug?.variants.find(
                    (item) => item.catalogId === catalogId
                  );
                  return variant ? describeVariant(variant) : '';
                })
                .join(', ')
            }
            notched
            MenuProps={{ sx: { maxHeight: 400 } }}
          >
            {sortedVariants.map((variant) => (
              <MenuItem value={variant.catalogId} key={variant.catalogId}>
                <Checkbox checked={includes(selectedCatalogIds, variant.catalogId)} />
                <ListItemText
                  primary={formatStrength(variant.strength)}
                  secondary={[
                    formatPackSize(variant.packSizeValue, variant.packSizeUnit),
                    formatForm(variant.formAlias, variant.dosageForm),
                  ]
                    .filter(Boolean)
                    .join(' ')}
                />
              </MenuItem>
            ))}
          </Select>
          <FormHelperText sx={{ mb: 1 }}>
            {submitted && selectedCatalogIds.length === 0 ? 'Required' : ''}
          </FormHelperText>
        </FormControl>

        <FormControl fullWidth variant='outlined' sx={{ mt: 1 }}>
          <InputLabel id='AddPartnerCatalogItemDialog-pharmacy-label' shrink>
            Compounding Pharmacy
          </InputLabel>
          <Select
            labelId='AddPartnerCatalogItemDialog-pharmacy-label'
            id='AddPartnerCatalogItemDialog-pharmacy-input'
            input={<OutlinedInput label='Compounding Pharmacy' />}
            value={compoundingPharmacyId}
            onChange={(event) => setCompoundingPharmacyId(event.target.value)}
            notched
            MenuProps={{ sx: { maxHeight: 400 } }}
          >
            <MenuItem value=''>
              <em>None</em>
            </MenuItem>
            {pharmacies.map((pharmacy) => (
              <MenuItem key={pharmacy.compoundingPharmacyId} value={pharmacy.compoundingPharmacyId}>
                {pharmacy.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <AlertPartnersCheckbox
          id='AddPartnerCatalogItemDialog-alert-partners'
          text={ALERT_PARTNER_CHECKBOX}
          checked={alertPartners}
          onChange={setAlertPartners}
          sx={{ mr: 'auto', ml: 1 }}
        />
        <Button variant='outlined' onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <LoadingButton loading={saving} variant='contained' onClick={handleSave} disabled={saving}>
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

AddPartnerCatalogItemDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  partner: PropTypes.shape({
    partnerUuid: PropTypes.string.isRequired,
  }).isRequired,
};

export default AddPartnerCatalogItemDialog;
